import { AuthenticatedRoutesGuard, useIdentityContext } from '@dark-portal/frontend-identity';
import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import { Loader } from '@/src/features/_components/loader/Loader';
import { APP } from '@/src/features/app/app.constants';

const LazyDashboardLayout = lazy(async () => {
  const { DashboardLayout } = await import('@/src/features/_components/layout/Layout');
  return { default: DashboardLayout };
});

const LazyPlanPage = lazy(async () => {
  const { PlanDashboardPage } = await import('@/src/features/modules/plan/pages/dashboard/Plan');
  return { default: PlanDashboardPage };
});

const LazyProjectsDashboardPage = lazy(async () => {
  const { ProjectsDashboardPage } = await import('@/src/features/modules/projects/pages/dashboard/Dashboard');
  return { default: ProjectsDashboardPage };
});

const LazyProjectPreviewLayout = lazy(async () => {
  const { ProjectPreviewLayout } = await import('@/src/features/modules/projects/pages/preview/layout/Layout');
  return { default: ProjectPreviewLayout };
});

const LazyProjectOverviewPage = lazy(async () => {
  const { ProjectOverviewPage } = await import('@/src/features/modules/projects/pages/preview/pages/overview/Overview');
  return { default: ProjectOverviewPage };
});

const LazyProjectDocumentationsPage = lazy(async () => {
  const { ProjectDocumentationsPage } =
    await import('@/src/features/modules/projects/pages/preview/pages/documentations/Documentations');
  return { default: ProjectDocumentationsPage };
});

const LazyProjectSharesPage = lazy(async () => {
  const { ProjectSharesPage } = await import('@/src/features/modules/projects/pages/preview/pages/shares/Shares');
  return { default: ProjectSharesPage };
});

const LazyProjectStagesPage = lazy(async () => {
  const { ProjectStagesPage } = await import('@/src/features/modules/projects/pages/preview/pages/stages/Stages');
  return { default: ProjectStagesPage };
});

const LazyProjectContractorsPage = lazy(async () => {
  const { ProjectContractorsPage } =
    await import('@/src/features/modules/projects/pages/preview/pages/contractors/Contractors');
  return { default: ProjectContractorsPage };
});

const LazyPreviewSettingsPage = lazy(async () => {
  const { PreviewSettingsPage } = await import('@/src/features/modules/settings/pages/preview/Preview');
  return { default: PreviewSettingsPage };
});

export const router = createBrowserRouter([
  {
    path: APP.ROUTES.ROOT,
    element: <Navigate to={APP.ROUTES.PLAN} replace />,
  },
  {
    element: (
      <AuthenticatedRoutesGuard>
        <LazyDashboardLayout />
      </AuthenticatedRoutesGuard>
    ),
    children: [
      { path: APP.ROUTES.PLAN, element: <LazyPlanPage /> },
      { path: APP.ROUTES.SETTINGS, element: <LazyPreviewSettingsPage /> },
      { path: APP.ROUTES.PROJECTS, element: <LazyProjectsDashboardPage /> },
      {
        path: APP.ROUTES.PROJECT,
        element: <LazyProjectPreviewLayout />,
        children: [
          { path: APP.ROUTES.PROJECT, element: <Navigate to="./overview" replace /> },
          { path: APP.ROUTES.PROJECT_OVERVIEW, element: <LazyProjectOverviewPage /> },
          { path: APP.ROUTES.PROJECT_DOCUMENTATIONS, element: <LazyProjectDocumentationsPage /> },
          { path: APP.ROUTES.PROJECT_SHARES, element: <LazyProjectSharesPage /> },
          { path: APP.ROUTES.PROJECT_STAGES, element: <LazyProjectStagesPage /> },
          { path: APP.ROUTES.PROJECT_CONTRACTORS, element: <LazyProjectContractorsPage /> },
        ],
      },
      { path: '*', element: <Navigate to={APP.ROUTES.PLAN} replace /> },
    ],
  },
]);

export const Router = (): React.JSX.Element => {
  const initialized = useIdentityContext((state) => state.initialized);

  return <Suspense fallback={<Loader />}>{initialized ? <RouterProvider router={router} /> : <Loader />}</Suspense>;
};
